import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { TopicSuggestionId } from "../id"
import { CreatedAt, UpdatedAt } from "./system"

export class TopicSuggestions {

  public static readonly TEXT_MIN_LENGTH = 1
  public static readonly TEXT_MAX_LENGTH = 30

  static toDocumentData(doc: TopicSuggestionDocument) {
    return {
      kind: doc.kind,
      text: doc.text,
      adoption_ratio: doc.adoption_ratio,
      status: doc.status,
      created_at: doc.created_at,
      updated_at: doc.updated_at,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): TopicSuggestionDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: TopicSuggestionId(doc.id),
      kind: data["kind"],
      text: data["text"],
      adoption_ratio: data["adoption_ratio"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const TopicKind = z.union([
  z.literal("talk").describe("一緒に話したい人"),
  z.literal("message").describe("メッセージ・画像"),
])

const TopicSuggestionStatus = z.enum(["public", "hidden"]).describe("公開状態")

export const TopicSuggestionValue = z.object({
  kind: TopicKind.describe("種別"),
  text: z.string()
    .min(TopicSuggestions.TEXT_MIN_LENGTH)
    .max(TopicSuggestions.TEXT_MAX_LENGTH)
    .describe("表示内容"),
  adoption_ratio: z.int().min(1).max(999).describe("表示倍率"),
  status: TopicSuggestionStatus,
})

export type TopicSuggestionValue = z.infer<typeof TopicSuggestionValue>

export const TopicSuggestionDocumentBody = z.intersection(
  TopicSuggestionValue,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type TopicSuggestionDocumentBody = z.infer<typeof TopicSuggestionDocumentBody>

export type TopicSuggestionDocument = TopicSuggestionDocumentBody & {
  id: TopicSuggestionId,
}

export const CreateTopicSuggestionRequest = z.object({
  kind: TopicKind,
  text: z.string()
    .min(TopicSuggestions.TEXT_MIN_LENGTH)
    .max(TopicSuggestions.TEXT_MAX_LENGTH),
  adoptionRatio: z.int().min(1).max(999),
})

export type CreateTopicSuggestionRequest = z.infer<typeof CreateTopicSuggestionRequest>

export const UpdateTopicSuggestionRequest = CreateTopicSuggestionRequest.extend({
  status: TopicSuggestionStatus,
})

export type UpdateTopicSuggestionRequest = z.infer<typeof UpdateTopicSuggestionRequest>
